<template>
    <router-link
        v-if="to !== null"
        :to="to"
        :class="linkClasses"
        :aria-label="collapsed ? (badge ? `${name}, ${badgeLabel}` : name) : undefined"
        :title="collapsed ? name : undefined"
        @pointerenter="preloadDestination"
        @pointerdown="preloadDestination"
        @focus="preloadDestination"
        @click="onClick"
    >
        <i :style="iconStyle" :class="iconClasses" aria-hidden="true"></i>
        <div v-if="!collapsed" :class="labelClasses">{{ name }}</div>
        <span v-if="badge" class="chat-badge" :aria-label="badgeLabel">{{
            badge > 99 ? "99+" : badge
        }}</span>
    </router-link>
    <button
        v-else-if="action"
        type="button"
        :class="linkClasses"
        :aria-label="collapsed ? name : undefined"
        :title="collapsed ? name : undefined"
        :data-cy="dataCy || undefined"
        @click="onClick"
    >
        <i :style="iconStyle" :class="iconClasses" aria-hidden="true"></i>
        <div v-if="!collapsed" :class="labelClasses">{{ name }}</div>
    </button>
    <a
        v-else
        :href="href || undefined"
        :class="linkClasses"
        :aria-label="collapsed ? name : undefined"
        :title="collapsed ? name : undefined"
        :data-cy="dataCy || undefined"
        @click="onClick"
    >
        <i :style="iconStyle" :class="iconClasses" aria-hidden="true"></i>
        <div v-if="!collapsed" :class="labelClasses">{{ name }}</div>
    </a>
</template>

<script>
import { preloadRoute } from "@/services/routePreload";

export default {
    props: {
        name: { type: String, required: true },
        icon: { type: String, required: true },
        route: { type: String, default: null },
        path: { type: String, default: null },
        href: { type: String, default: null },
        action: { type: String, default: null },
        isMobile: { type: Boolean, default: false },
        block: { type: Boolean, default: false },
        collapsed: { type: Boolean, default: false },
        badge: { type: Number, default: 0 },
        dataCy: { type: String, default: null }
    },
    emits: ["click", "action"],
    computed: {
        badgeLabel() {
            return `${this.badge} unread ${this.badge === 1 ? "conversation" : "conversations"}`;
        },
        to() {
            if (this.route) return { name: this.route };
            if (this.path) return this.path;
            return null;
        },
        linkClasses() {
            const themeClass = this.isMobile ? "side-nav-item--mobile" : "side-nav-item--desktop";
            const baseClasses = ["side-nav-item", themeClass];
            if (this.collapsed) {
                return [
                    ...baseClasses,
                    "side-nav-item--collapsed w-100 d-flex align-items-center justify-content-center text-decoration-none py-2"
                ];
            }
            if (this.isMobile) {
                return [
                    ...baseClasses,
                    "w-100 d-flex align-items-center gap-3 text-decoration-none px-3 py-2"
                ];
            }
            if (this.block) {
                return [
                    ...baseClasses,
                    "w-100 text-decoration-none hstack justify-content-start gap-2 px-2 py-2"
                ];
            }
            return [
                ...baseClasses,
                "col text-decoration-none",
                "w-100 hstack align-items-center justify-content-start gap-2 px-2 py-2"
            ];
        },
        iconClasses() {
            return ["nav-icon", "fas", `fa-${this.icon}`];
        },
        iconStyle() {
            if (this.collapsed) return "";
            return this.isMobile ? "" : "width: 1.25rem";
        },
        labelClasses() {
            return ["nav-label"];
        }
    },
    methods: {
        preloadDestination() {
            if (this.to !== null) preloadRoute(this.$router, this.to);
        },
        onClick(event) {
            try {
                this.$gtag.event("click:nav-item", {
                    name: this.name,
                    domain: this.$store.getters.domain,
                    routeName: this.route
                });
            } catch (error) {
                //
            }

            // Retain keyboard focus; pointer navigation need not leave a focus ring.
            if (event.detail > 0) event.currentTarget?.blur();

            if (this.action) {
                event.preventDefault?.();
                this.$emit("action", this.action);
            }
            this.$emit("click");
        }
    }
};
</script>

<style scoped>
.side-nav-item {
    position: relative;
    color: var(--side-nav-text, #ffffff);
    background-color: transparent;
    border: 0;
    border-radius: var(--forkto-radius-compact);
    font: inherit;
    line-height: inherit;
    text-align: left;
    transition:
        color 0.15s ease-in-out,
        background-color 0.15s ease-in-out;
}

.side-nav-item:hover {
    color: var(--side-nav-active-text, #0e0e0e);
    background-color: var(--side-nav-hover, #ffffff);
}

.side-nav-item:focus-visible {
    color: var(--side-nav-active-text, #0e0e0e);
    background-color: var(--side-nav-hover, #ffffff);
    outline: 2px solid var(--forkto-focus-ring);
    outline-offset: -2px;
}

.side-nav-item:active {
    animation: clickAnimation 0.1s linear;
    animation-play-state: running;
}

@keyframes clickAnimation {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(0.95);
    }
    100% {
        transform: scale(1);
    }
}

.nav-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25em;
    height: 1.25em;
    font-size: 24px;
    color: currentColor;
    line-height: 1;
    transform: translateY(-0.0667em);
}

@media screen and (min-width: 1280px) {
    .nav-icon {
        font-size: 16px;
    }
}

.side-nav-item--desktop .nav-icon {
    color: var(--side-nav-text, var(--forkto-ink-2));
}

/* Labels lead in the expanded sidebar; the collapsed rail keeps larger icons. */
.side-nav-item--desktop:not(.side-nav-item--collapsed) .nav-icon {
    font-size: 0.875rem;
}

.side-nav-item--desktop:hover .nav-icon,
.side-nav-item--desktop:focus-visible .nav-icon,
.side-nav-item--desktop.router-link-active .nav-icon {
    color: var(--side-nav-active-text, var(--forkto-ink-1));
}

.nav-label {
    color: currentColor;
    line-height: 1;
    white-space: nowrap;
}

.side-nav-item--mobile {
    min-height: 48px;
    font-size: 15px;
    font-weight: 500;
}

.side-nav-item--mobile .nav-icon {
    flex: 0 0 20px;
    width: 20px;
    font-size: 18px;
    transform: none;
}

.side-nav-item--mobile .nav-label {
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.35;
}

.side-nav-item--mobile:focus-visible {
    outline-color: var(--forkto-ink-3);
}

.side-nav-item--desktop {
    font-weight: 500;
}

.side-nav-item--desktop.router-link-active {
    color: var(--side-nav-active-text, var(--forkto-ink-1));
    background-color: var(--side-nav-active, var(--forkto-accent-soft));
    box-shadow: inset 3px 0 0 var(--forkto-accent, #15181a);
    font-weight: 600;
}

.side-nav-item--mobile.router-link-active {
    background-color: var(--side-nav-active, var(--forkto-surface));
    color: var(--side-nav-active-text, var(--forkto-ink-1));
    box-shadow: inset 3px 0 0 var(--forkto-accent);
    font-weight: 600;
}

@media (prefers-reduced-motion: reduce) {
    .side-nav-item {
        transition: none;
    }

    .side-nav-item:active {
        animation: none;
    }
}
</style>
